import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Icon } from "@/lib/icons";
import type { ImportStatusFilter } from "./excel-file-monitor";

interface ExcelFilesFiltersProps {
  cancelLabel: string;
  filterByLanguageLabel: string;
  allLanguagesLabel: string;
  filterByStatusLabel: string;
  allStatusesLabel: string;
  pendingLabel: string;
  approvedLabel: string;
  rejectedLabel: string;
  deleteAllLabel: string;
  confirmTitleLabel: string;
  deleteAllDescriptionLabel: string;
  confirmLabel: string;
  selectedLang: string;
  selectedStatus: ImportStatusFilter;
  uniqueLangs: string[];
  onLangChange: (lang: string) => void;
  onStatusChange: (status: ImportStatusFilter) => void;
  onDeleteAll: () => void;
  isDeletingAll: boolean;
}

export function ExcelFilesFilters({
  cancelLabel,
  filterByLanguageLabel,
  allLanguagesLabel,
  filterByStatusLabel,
  allStatusesLabel,
  pendingLabel,
  approvedLabel,
  rejectedLabel,
  deleteAllLabel,
  confirmTitleLabel,
  deleteAllDescriptionLabel,
  confirmLabel,
  selectedLang,
  selectedStatus,
  uniqueLangs,
  onLangChange,
  onStatusChange,
  onDeleteAll,
  isDeletingAll,
}: ExcelFilesFiltersProps) {
  return (
    <section className="pt-7">
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-[200px]">
          <Select value={selectedLang} onValueChange={onLangChange}>
            <SelectTrigger className="border-border/40 bg-muted/20 text-foreground/80">
              <SelectValue placeholder={filterByLanguageLabel} />
            </SelectTrigger>
            <SelectContent className="border-border/40 bg-card">
              <SelectItem value="all">{allLanguagesLabel}</SelectItem>
              {uniqueLangs.map((lang) => (
                <SelectItem key={lang} value={lang}>
                  {lang}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-[200px]">
          <Select
            value={selectedStatus}
            onValueChange={(value) => {
              if (
                value === "all" ||
                value === "PENDING" ||
                value === "APPROVED" ||
                value === "REJECTED"
              ) {
                onStatusChange(value);
              }
            }}
          >
            <SelectTrigger className="border-border/40 bg-muted/20 text-foreground/80">
              <SelectValue placeholder={filterByStatusLabel} />
            </SelectTrigger>
            <SelectContent className="border-border/40 bg-card">
              <SelectItem value="all">{allStatusesLabel}</SelectItem>
              <SelectItem value="PENDING">{pendingLabel}</SelectItem>
              <SelectItem value="APPROVED">{approvedLabel}</SelectItem>
              <SelectItem value="REJECTED">{rejectedLabel}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {selectedLang !== "all" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-full border border-danger/20 bg-transparent px-3 text-xs text-danger hover:bg-danger/10 hover:text-danger"
              >
                <Icon name="trash2" className="mr-2 h-3.5 w-3.5" />
                {deleteAllLabel.replace("{lang}", selectedLang)}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="border-border/40 bg-card">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-foreground">{confirmTitleLabel}</AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground">
                  {deleteAllDescriptionLabel.replace("{lang}", selectedLang)}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-border/40 bg-transparent text-muted-foreground hover:bg-muted/20">
                  {cancelLabel}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDeleteAll}
                  disabled={isDeletingAll}
                  className="bg-danger/20 text-danger hover:bg-danger/30"
                >
                  {confirmLabel}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </section>
  );
}
